import React from 'react';
import PropTypes from 'prop-types';
import LazyImage from 'dibs-react-lazy-image';
import { TrackingCarousel } from '../../components/global/TrackingCarousel';
import {
    trackInitialDisplay,
    trackArrowClick,
    trackItemClick,
    trackRecentItems,
} from 'dibs-recent-history/exports/recentHistoryTracking';
import { useUID } from 'dibs-uid';
import { Link } from 'dibs-elements/exports/Link';
import {
    HORIZONTAL_OFFSET_RESP_IMG,
    VERTICAL_OFFSET_RESP_IMG,
    SRC_SET_SIZES_RESP_RECENTLY_VIEWED_TILE,
} from '../../constants/imageConstants';
import { getSrcSetString } from 'dibs-search-product-tile/exports/srcSetHelpers';

export function handleInitialDisplay(items) {
    return function() {
        const itemsReturned = items.length;
        trackInitialDisplay({ itemsReturned });
    };
}

const SharedRecentlyViewedItemsCarousel = props => {
    const uid = useUID();

    function handleItemClick({ item, index }) {
        trackItemClick();
        trackRecentItems({
            type: 'productClick',
            items: [item],
            startIndex: index + 1, // Tracking needs non zero index position.
            variant: 'products',
        });
    }

    function handleItemImpression({ item, index }) {
        trackRecentItems({
            type: 'productImpression',
            items: [item],
            startIndex: index + 1, // Tracking needs non zero index position.
            variant: 'products',
        });
    }

    // this is not a standard react component function--do not force proptype validation!
    // eslint-disable-next-line react/prop-types
    function renderCarouselItem({ index }) {
        const { items, classNames } = props;
        const item = items[index];
        const itemImageUrlSrc = item.itemImageUrl?.src || item.itemImageUrl;

        const srcSetAttributes = itemImageUrlSrc
            ? {
                  srcSet: getSrcSetString(itemImageUrlSrc),
                  sizes: SRC_SET_SIZES_RESP_RECENTLY_VIEWED_TILE,
              }
            : {};
        const key = `recent-${item.itemId}`;
        const id = `${uid}-recent-${item.itemId}`;
        return (
            <Link
                key={key}
                id={id}
                href={item.itemPdpUrl}
                onClick={() => handleItemClick({ item, index })}
                className={classNames.itemLink}
                dataTn="recently-viewed-link"
                // use link label instead of img alt bc lazy img will not be rendered on page load
                // therefor alt text/ will not exist on page load.
                ariaLabel={item.itemTitle}
            >
                <LazyImage
                    offsetHorizontal={HORIZONTAL_OFFSET_RESP_IMG}
                    offsetVertical={VERTICAL_OFFSET_RESP_IMG}
                    placeholderClass={classNames.placeholder}
                    className={classNames.placeholder}
                >
                    <img
                        aria-labelledby={id}
                        className={classNames.itemImage}
                        src={itemImageUrlSrc}
                        alt=""
                        {...srcSetAttributes}
                    />
                </LazyImage>
            </Link>
        );
    }

    const { items, itemsPerPage, classNames } = props;

    return (
        <TrackingCarousel
            items={items}
            classNames={classNames}
            dataTn="recently-viewed-carousel"
            renderItem={renderCarouselItem}
            itemsPerPage={itemsPerPage}
            onInitialDisplay={handleInitialDisplay(items)}
            onItemImpression={handleItemImpression}
            onNextPage={() => trackArrowClick({ direction: 'right' })}
            onPreviousPage={() => trackArrowClick({ direction: 'left' })}
        />
    );
};

SharedRecentlyViewedItemsCarousel.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            contemporaryTrackingString: PropTypes.string,
            itemTitle: PropTypes.string.isRequired,
            itemId: PropTypes.string.isRequired,
            itemBrowseUrl: PropTypes.string,
            itemCategoryCode: PropTypes.string,
            itemImageUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
            itemPdpUrl: PropTypes.string.isRequired,
            dealerPk: PropTypes.string.isRequired,
            category: PropTypes.string.isRequired,
        })
    ).isRequired,
    itemsPerPage: PropTypes.number.isRequired,
    classNames: PropTypes.shape({
        list: PropTypes.string,
        placeholder: PropTypes.string.isRequired,
        itemLink: PropTypes.string.isRequired,
        itemImage: PropTypes.string.isRequired,
    }).isRequired,
};

export { SharedRecentlyViewedItemsCarousel };
